import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';

import {
    FiUpload, FiX, FiDownload, FiAlertCircle,
    FiCheckCircle,
    FiCheck,
    FiSearch,
    FiFileText,
    FiPackage,
    FiArrowRight,
    FiRefreshCw,
    FiInfo,
    FiChevronDown,
} from 'react-icons/fi';
import "./AddCsv.css"
import { BsFileSpreadsheet, BsClipboardData, BsGrid3X3Gap, BsBox, } from 'react-icons/bs';
import { BiLoaderAlt } from 'react-icons/bi';
import { MdBarChart } from 'react-icons/md';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import Swal from 'sweetalert2';
import { productAPI } from '../../api';
import './Product.css';


/* =========================================================
   CONSTANTS
========================================================= */

const CSV_HEADERS = [
    'Products',
    'sku',
    'MRP',
    'Selling Price',
    'Discount Percent',
    'Tax Percent (GST)',
    'GST Amount',
    'HSN No',
    'Net Payment',
    'Stock Quantity',
    'Warranty (Months)',
    'Replacement Days',
    'Payment Options',
];

const INSTRUCTIONS = [
    ['📌 INSTRUCTIONS: Fill in the YELLOW columns. Blue columns (Product Name, SKU, Selling Price, GST Amount & Net Payment) are locked.'],
    ['• MRP: Maximum Retail Price of the product (Mandatory)'],
    ['• Discount Percent: Enter discount % (0 to 100) (Mandatory). Changing Discount Percent automatically updates Selling Price.'],
    ['• Selling Price: Automatically calculated based on MRP and Discount Percent (Locked, cannot be edited directly)'],
    ['• Tax Percent (GST): Select applicable GST rate from dropdown'],
    ['• GST Amount: Automatically calculated based on Selling Price and GST (Locked)'],
    ['• HSN No: HSN number for the product (4 to 10 digits)'],
    ['• Net Payment: Automatically calculated based on Selling Price and GST Amount (Locked)'],
    ['• Stock Quantity: Available stock count (Mandatory)'],
    ['• Warranty (Months): Select warranty period in months from dropdown'],
    ['• Replacement Days: Select replacement days from dropdown (e.g., 7, 10, 12, 14)'],
    ['• Payment Options: Online, Cash on Delivery (comma separated)'],
    [''],
];


/* =========================================================
   TEMPLATE DOWNLOAD
========================================================= */

const downloadTemplate = async (
    format: 'csv' | 'xlsx',
    selectedProducts?: any[]
) => {
    const toastId = 'template-download';
    try {
        toast.loading('Preparing template...', { id: toastId });
        let templateData: any[] = [];

        /* -----------------------------------------------------
           Selected Products
        ----------------------------------------------------- */

        if (selectedProducts && selectedProducts.length > 0) {
            templateData = selectedProducts.map((p: any) => {
                const mrp = p.mrp || p.price || '';
                const sellingPrice = p.selling_price || p.price || '';
                let discount = p.discount || p.discount_percentage || '';
                if (!discount && mrp && sellingPrice && Number(mrp) > 0) {
                    discount = Math.round(((Number(mrp) - Number(sellingPrice)) / Number(mrp)) * 100 * 100) / 100;
                }
                return {
                    Products: p.name || '',
                    sku: p.sku || '',
                    MRP: mrp,
                    'Selling Price': sellingPrice,
                    'Discount Percent': discount,
                    'Tax Percent (GST)': p.tax ?? '',
                    'HSN No': p.hsn || '',
                    'Net Payment': p.netAmount ?? '',
                    'Stock Quantity': p.stock ?? '',
                    'Warranty (Months)': p.warranty ?? '',
                    'Replacement Days': p.replacementDays || p.replacement_days || '',
                    'Payment Options': Array.isArray(p.paymentOptions) ? p.paymentOptions.join(', ') : (p.paymentOptions || 'Online, Cash on Delivery'),
                };
            });
        }

        /* -----------------------------------------------------
           Fetch Products
        ----------------------------------------------------- */

        else {
            try {
                const res = await productAPI.getProductsTemplateData();

                if (
                    res.data &&
                    res.data.response === 'S' &&
                    Array.isArray(res.data.data) &&
                    res.data.data.length > 0
                ) {
                    templateData = res.data.data.map((p: any) => {
                        const mrp = p.mrp || p.price || '';
                        const sellingPrice = p.selling_price || p.price || '';
                        let discount = p.discount || p.discount_percentage || '';
                        if (!discount && mrp && sellingPrice && Number(mrp) > 0) {
                            discount = Math.round(((Number(mrp) - Number(sellingPrice)) / Number(mrp)) * 100 * 100) / 100;
                        }
                        return {
                            Products: p.name || '',
                            sku: p.sku || '',
                            MRP: mrp,
                            'Selling Price': sellingPrice,
                            'Discount Percent': discount,
                            'Tax Percent (GST)': p.tax ?? '',
                            'HSN No': p.hsn || '',
                            'Net Payment': p.netAmount ?? '',
                            'Stock Quantity': p.stock ?? '',
                            'Warranty (Months)': p.warranty ?? '',
                            'Replacement Days': p.replacementDays || p.replacement_days || '',
                            'Payment Options': Array.isArray(p.paymentOptions) ? p.paymentOptions.join(', ') : (p.paymentOptions || 'Online, Cash on Delivery'),
                        };
                    });
                }
            } catch (error) {
                console.error('Template product fetch failed:', error);
            }
        }

        /* -----------------------------------------------------
           Fallback
        ----------------------------------------------------- */

        if (templateData.length === 0) {
            templateData = [
                {
                    Products: 'Sample Product',
                    sku: 'DS-2026-BB1650',
                    MRP: '',
                    'Selling Price': '',
                    'Discount Percent': '',
                    'Tax Percent (GST)': '',
                    'HSN No': '',
                    'Net Payment': '',
                    'Stock Quantity': '',
                    'Warranty (Months)': '',
                    'Replacement Days': '',
                    'Payment Options': 'Online, Cash on Delivery',
                },
            ];
        }

        /* =====================================================
           CSV
        ===================================================== */

        if (format === 'csv') {
            const csvRows = [CSV_HEADERS.join(',')];
            for (const row of templateData) {
                const values = CSV_HEADERS.map((header) => {
                    let value = row[header] ?? '';
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        value = `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                });
                csvRows.push(values.join(','));
            }
            const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;', });
            const url = URL.createObjectURL(blob);

            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = 'products_template.csv';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
            toast.success('CSV Template downloaded!', { id: toastId, });
            return;
        }
        const workbook = new ExcelJS.Workbook();
        workbook.calcProperties.fullCalcOnLoad = true;
        const worksheet = workbook.addWorksheet('Template');

        const totalCols = CSV_HEADERS.length;

        for (const instructionRow of INSTRUCTIONS) {
            const row = worksheet.addRow(
                instructionRow
            );

            worksheet.mergeCells(
                row.number,
                1,
                row.number,
                totalCols
            );

            const cell = row.getCell(1);

            cell.font = {
                italic: true,
                color: {
                    argb: 'FF64748B',
                },
                size: 10,
            };

            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: {
                    argb: 'FFF8FAFC',
                },
            };

            cell.alignment = {
                wrapText: true,
            };

            cell.protection = {
                locked: true,
            };
        }

        /* -----------------------------------------------------
           Header
        ----------------------------------------------------- */

        const headerRow = worksheet.addRow(
            CSV_HEADERS
        );

        const instructionRowCount =
            INSTRUCTIONS.length;

        const headerRowNumber =
            instructionRowCount + 1;

        CSV_HEADERS.forEach(
            (_, colIndex) => {
                const cell =
                    headerRow.getCell(
                        colIndex + 1
                    );

                const isLockedColumn =
                    colIndex === 0 ||
                    colIndex === 1 ||
                    colIndex === 3 ||
                    colIndex === 6 ||
                    colIndex === 8;

                cell.font = {
                    bold: true,
                    color: {
                        argb: isLockedColumn
                            ? 'FF1E3A5F'
                            : 'FF0F172A',
                    },
                };

                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: {
                        argb: isLockedColumn
                            ? 'FFDCEBFE'
                            : 'FFFFF7C2',
                    },
                };

                cell.border = {
                    top: {
                        style: 'medium',
                        color: {
                            argb: 'FFCBD5E1',
                        },
                    },
                    bottom: {
                        style: 'medium',
                        color: {
                            argb: 'FFCBD5E1',
                        },
                    },
                    left: {
                        style: 'thin',
                        color: {
                            argb: 'FFE2E8F0',
                        },
                    },
                    right: {
                        style: 'thin',
                        color: {
                            argb: 'FFE2E8F0',
                        },
                    },
                };

                cell.protection = {
                    locked: true,
                };

                cell.alignment = {
                    horizontal: 'center',
                    vertical: 'middle',
                };
            }
        );

        /* -----------------------------------------------------
           Data Rows
        ----------------------------------------------------- */

        for (
            let rowIndex = 0;
            rowIndex < templateData.length;
            rowIndex++
        ) {
            const rowData =
                templateData[rowIndex];

            const dataRowNumber =
                headerRowNumber +
                rowIndex +
                1;

            const dataRow =
                worksheet.addRow(
                    CSV_HEADERS.map(
                        (header) =>
                            rowData[header] ?? ''
                    )
                );

            dataRow.height = 22;

            CSV_HEADERS.forEach(
                (_, colIndex) => {
                    const cell =
                        dataRow.getCell(
                            colIndex + 1
                        );

                    const isLockedColumn =
                        colIndex === 0 ||
                        colIndex === 1 ||
                        colIndex === 3 ||
                        colIndex === 6 ||
                        colIndex === 8;

                    cell.protection = {
                        locked: isLockedColumn,
                    };

                    const spVal = rowData['Selling Price'] !== undefined && rowData['Selling Price'] !== '' ? rowData['Selling Price'] : '';
                    const discVal = rowData['Discount Percent'] !== undefined && rowData['Discount Percent'] !== '' ? rowData['Discount Percent'] : '';

                    const hasSp = spVal !== '' && !isNaN(Number(spVal));
                    const hasDisc = discVal !== '' && !isNaN(Number(discVal));

                    /* MRP Validation */
                    if (colIndex === 2) {
                        cell.dataValidation = {
                            type: 'decimal',
                            operator: 'greaterThanOrEqual',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'error',
                            errorTitle: 'Invalid MRP',
                            error: 'MRP must be a Number and valid number greater than 0.',
                            formulae: [0],
                        };
                    }

                    /* Selling Price (Locked, auto-calculated from MRP and Discount Percent) */
                    else if (colIndex === 3) {
                        const precomputedSp = hasSp
                            ? Number(spVal)
                            : (hasDisc && rowData.MRP && Number(rowData.MRP) > 0
                                ? Math.round(Number(rowData.MRP) * (1 - Number(discVal) / 100) * 100) / 100
                                : undefined);

                        cell.value = {
                            formula:
                                `IF(AND(ISNUMBER(C${dataRowNumber}),C${dataRowNumber}>0,ISNUMBER(E${dataRowNumber}),E${dataRowNumber}>=0),ROUND(C${dataRowNumber}*(1-E${dataRowNumber}/100),2),"")`,
                            result: precomputedSp,
                        };
                    }

                    /* Discount Percent (Editable Input: 0 to 100) */

                    else if (colIndex === 4) {
                        cell.dataValidation = {
                            type: 'decimal',
                            operator: 'between',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'error',
                            errorTitle: 'Invalid Discount Percent',
                            error: 'Discount Percent must be between 0 and 100.',
                            formulae: [
                                '0',
                                '100',
                            ],
                        };
                        cell.value = hasDisc ? Number(discVal) : '';
                    }

                    /* GST Validation */

                    else if (colIndex === 5) {
                        cell.dataValidation = {
                            type: 'list',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'error',
                            errorTitle:
                                'Invalid GST Rate',
                            error:
                                'Please enter only one of the following values: 0, 0.25, 3, 5, 12, 18, or 28.',
                            formulae: [
                                '"0,0.25,3,5,12,18,28"',
                            ],
                        };
                    }

                    /* GST Amount Calculation */
                    else if (colIndex === 6) {
                        const taxVal = rowData['Tax Percent (GST)'] !== undefined && rowData['Tax Percent (GST)'] !== '' ? rowData['Tax Percent (GST)'] : '';
                        const hasTax = taxVal !== '' && !isNaN(Number(taxVal));
                        const precomputedGst = hasSp && hasTax
                            ? Math.round(Number(spVal) * (Number(taxVal) / 100) * 100) / 100
                            : undefined;

                        cell.value = {
                            formula:
                                `IF(AND(ISNUMBER(D${dataRowNumber}),D${dataRowNumber}>0,ISNUMBER(F${dataRowNumber}),F${dataRowNumber}>=0),ROUND(D${dataRowNumber}*(F${dataRowNumber}/100),2),"")`,
                            result: precomputedGst,
                        };
                    }

                    /* HSN Validation */

                    else if (colIndex === 7) {
                        cell.dataValidation = {
                            type: 'textLength',
                            operator: 'between',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'error',
                            errorTitle: 'Invalid HSN No',
                            error: 'HSN No must be between 4 and 10 digits.',
                            formulae: [4, 10],
                        };
                        cell.value = rowData['HSN No'] ?? '';
                    }

                    /* Net Payment Calculation */
                    else if (colIndex === 8) {
                        const taxVal = rowData['Tax Percent (GST)'] !== undefined && rowData['Tax Percent (GST)'] !== '' ? rowData['Tax Percent (GST)'] : '';
                        const hasTax = taxVal !== '' && !isNaN(Number(taxVal));
                        const precomputedNet = hasSp && hasTax
                            ? Math.round(Number(spVal) * (1 + Number(taxVal) / 100) * 100) / 100
                            : undefined;

                        cell.value = {
                            formula:
                                `IF(AND(ISNUMBER(D${dataRowNumber}),D${dataRowNumber}>0,ISNUMBER(G${dataRowNumber}),G${dataRowNumber}>=0),ROUND(D${dataRowNumber}+G${dataRowNumber},2),"")`,
                            result: precomputedNet,
                        };
                    }

                    /* Stock Quantity Validation */
                    else if (colIndex === 9) {
                        cell.dataValidation = {
                            type: 'whole',
                            operator: 'greaterThanOrEqual',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'error',
                            errorTitle: 'Invalid Stock Quantity',
                            error: 'Stock Quantity must be a whole number (0 or greater).',
                            formulae: [0],
                        };
                    }

                    /* Warranty Validation */
                    else if (colIndex === 10) {
                        cell.dataValidation = {
                            type: 'whole',
                            operator: 'greaterThanOrEqual',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'error',
                            errorTitle: 'Invalid Warranty',
                            error: 'Warranty must be a whole number (0 or greater).',
                            formulae: [0],
                        };
                    }

                    /* Replacement Days Validation */

                    else if (colIndex === 11) {
                        cell.dataValidation = {
                            type: 'list',
                            allowBlank: true,
                            showErrorMessage: true,
                            errorStyle: 'warning',
                            errorTitle:
                                'Custom Replacement Days',
                            error:
                                'You selected a number of replacement days not in the dropdown. This is allowed, but verify it is correct.',
                            formulae: [
                                '"0,7,10,12,14,30"',
                            ],
                        };
                    }

                    cell.border = {
                        top: {
                            style: 'thin',
                            color: {
                                argb: 'FFE2E8F0',
                            },
                        },
                        bottom: {
                            style: 'thin',
                            color: {
                                argb: 'FFE2E8F0',
                            },
                        },
                        left: {
                            style: 'thin',
                            color: {
                                argb: 'FFE2E8F0',
                            },
                        },
                        right: {
                            style: 'thin',
                            color: {
                                argb: 'FFE2E8F0',
                            },
                        },
                    };

                    cell.alignment = {
                        vertical: 'middle',
                    };

                    if (isLockedColumn) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: {
                                argb: 'FFEFF6FF',
                            },
                        };

                        cell.font = {
                            bold: true,
                            color: {
                                argb: 'FF1E3A5F',
                            },
                        };
                    } else {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: {
                                argb: 'FFFFFEF2',
                            },
                        };
                    }
                }
            );
        }

        /* -----------------------------------------------------
           Column Width
        ----------------------------------------------------- */

        worksheet.columns.forEach(
            (column, index) => {
                column.width =
                    index === 0
                        ? 35
                        : index === 1
                            ? 20
                            : 22;
            }
        );

        /* -----------------------------------------------------
           Freeze
        ----------------------------------------------------- */

        worksheet.views = [
            {
                state: 'frozen',
                xSplit: 0,
                ySplit: headerRowNumber,
            },
        ];

        /* -----------------------------------------------------
           Protect
        ----------------------------------------------------- */

        await worksheet.protect(
            'DecsoPlus123',
            {
                selectLockedCells: true,
                selectUnlockedCells: true,
                formatCells: false,
                formatColumns: false,
                formatRows: false,
            }
        );

        /* -----------------------------------------------------
           Download
        ----------------------------------------------------- */

        const buffer = await workbook.xlsx.writeBuffer();

        let blob: Blob;
        try {
            const zip = await JSZip.loadAsync(buffer);
            let wbXml = await zip.file('xl/workbook.xml')?.async('text');
            if (wbXml) {
                if (/<calcPr[^>]*\/>/.test(wbXml)) {
                    wbXml = wbXml.replace(
                        /<calcPr[^>]*\/>/,
                        '<calcPr calcId="171027" fullCalcOnLoad="1" iterate="1" maxIterations="100" maxChange="0.001"/>'
                    );
                } else if (/<calcPr[^>]*>[\s\S]*?<\/calcPr>/.test(wbXml)) {
                    wbXml = wbXml.replace(
                        /<calcPr[^>]*>[\s\S]*?<\/calcPr>/,
                        '<calcPr calcId="171027" fullCalcOnLoad="1" iterate="1" maxIterations="100" maxChange="0.001"/>'
                    );
                } else {
                    wbXml = wbXml.replace(
                        '</workbook>',
                        '<calcPr calcId="171027" fullCalcOnLoad="1" iterate="1" maxIterations="100" maxChange="0.001"/></workbook>'
                    );
                }
                zip.file('xl/workbook.xml', wbXml);
                blob = await zip.generateAsync({
                    type: 'blob',
                    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
            } else {
                blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
            }
        } catch (zipErr) {
            console.warn('Could not inject calcPr iterate via JSZip:', zipErr);
            blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        }

        const url =
            URL.createObjectURL(blob);

        const anchor =
            document.createElement('a');

        anchor.href = url;
        anchor.download =
            'products_template.xlsx';

        document.body.appendChild(anchor);

        anchor.click();

        anchor.remove();

        URL.revokeObjectURL(url);

        toast.success(
            'Excel Template downloaded!',
            {
                id: toastId,
            }
        );

    } catch (error) {
        console.error(
            'downloadTemplate error:',
            error
        );

        toast.error(
            'Failed to download template. Please try again.',
            {
                id: toastId,
            }
        );
    }
};


/* =========================================================
   CSV UPLOAD PANEL
========================================================= */

function CSVUploadPanel() {
    const navigate =
        useNavigate();

    const [
        csvFile,
        setCsvFile,
    ] = useState<File | null>(null);

    const [
        uploading,
        setUploading,
    ] = useState(false);

    const [
        result,
        setResult,
    ] = useState<{
        success?: number;
        failed?: number;
        errors?: string[];
    } | null>(null);

    const userDetails =
        JSON.parse(
            localStorage.getItem(
                'seller'
            ) || '{}'
        );


    /* -------------------------------------------------------
       Dropzone
    ------------------------------------------------------- */

    const onDrop =
        useCallback(
            (acceptedFiles: File[]) => {
                if (
                    acceptedFiles.length > 0
                ) {
                    setCsvFile(
                        acceptedFiles[0]
                    );

                    setResult(null);
                }
            },
            []
        );


    const {
        getRootProps,
        getInputProps,
        isDragActive,
    } =
        useDropzone({
            onDrop,

            accept: {
                'text/csv': [
                    '.csv',
                ],

                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                    ['.xlsx'],

                'application/vnd.ms-excel':
                    ['.xls'],
            },

            maxFiles: 1,
        });


    /* -------------------------------------------------------
       Upload
    ------------------------------------------------------- */

    const handleUpload =
        async () => {

            if (!csvFile) {
                toast.error(
                    'Select a CSV or Excel file first'
                );

                return;
            }

            setUploading(true);
            setResult(null);

            try {

                const formData =
                    new FormData();

                formData.append(
                    'file',
                    csvFile
                );

                formData.append(
                    'seller_id',
                    userDetails._id
                );

                const response =
                    await productAPI.uploadCSV(
                        formData
                    );

                const data =
                    response.data;

                const uploadResult = {
                    success:
                        data.successCount ??
                        data.inserted ??
                        0,

                    failed:
                        data.failedCount ??
                        data.failed ??
                        0,

                    errors:
                        data.errors ??
                        [],
                };

                setResult(
                    uploadResult
                );


                /* -------------------------------------------------
                   Helper to render error details HTML in modal
                ------------------------------------------------- */

                const renderErrorsModalHtml = (errors: string[]) => {
                    if (!errors || errors.length === 0) return '';
                    const itemsHtml = errors.map((err: string, idx: number) => {
                        const cellMatch = err.match(/\(([A-Z]+[0-9]+)\)/);
                        const cellBadge = cellMatch 
                            ? `<span style="background:#fee2e2;color:#b91c1c;font-weight:800;font-size:11.5px;padding:2px 7px;border-radius:4px;border:1px solid #fca5a5;margin-right:6px;letter-spacing:0.5px;">${cellMatch[1]}</span>` 
                            : '';
                        const cleanErr = cellMatch ? err.replace(/\(([A-Z]+[0-9]+)\)\s*/, '') : err;
                        return `
                            <div style="display:flex;align-items:flex-start;gap:8px;padding:8px 10px;margin-bottom:6px;background:#ffffff;border:1px solid #fee2e2;border-left:3px solid #ef4444;border-radius:6px;text-align:left;font-size:12.5px;color:#1e293b;line-height:1.4;box-shadow:0 1px 2px rgba(0,0,0,0.04);">
                                <span style="color:#94a3b8;font-size:11px;font-weight:700;margin-top:2px;">${idx + 1}.</span>
                                <div style="flex:1;min-width:0;">
                                    ${cellBadge}
                                    <span style="font-weight:600;color:#991b1b;">${cleanErr}</span>
                                </div>
                            </div>
                        `;
                    }).join('');

                    return `
                        <div style="margin-top:14px;max-height:240px;overflow-y:auto;padding-right:4px;background:#f8fafc;border-radius:8px;padding:10px;border:1px solid #e2e8f0;">
                            <div style="font-weight:700;font-size:11.5px;color:#475569;margin-bottom:8px;text-align:left;text-transform:uppercase;letter-spacing:0.5px;">
                                ⚠️ Missing / Invalid Cell Locations (${errors.length}):
                            </div>
                            ${itemsHtml}
                        </div>
                    `;
                };

                /* -------------------------------------------------
                   Success
                ------------------------------------------------- */

                if (uploadResult.success > 0) {
                    Swal.fire({
                        icon: uploadResult.failed > 0 ? 'warning' : 'success',
                        title: uploadResult.failed > 0 ? 'Partial Upload Complete' : 'Upload Successful!',
                        html: `
                            <div style="text-align:left;font-size:14px;color:#334155;">
                                <strong style="color:#16a34a;">${uploadResult.success}</strong> products were successfully added or updated!
                                ${uploadResult.failed > 0 ? `<br/><br/><span style="color:#dc2626;font-weight:600">${uploadResult.failed} products failed due to missing fields.</span>` : ''}
                            </div>
                            ${renderErrorsModalHtml(uploadResult.errors)}
                        `,
                        width: uploadResult.errors.length > 0 ? '600px' : '480px',
                        confirmButtonColor: '#2563EB',
                        confirmButtonText: uploadResult.failed > 0 ? 'OK' : 'Go to Products',
                        customClass: {
                            popup: 'premium-swal-popup',
                        },
                    }).then((swalResult) => {
                        if (swalResult.isConfirmed && uploadResult.failed === 0) {
                            navigate('/products');
                        }
                    });
                }

                /* -------------------------------------------------
                   Failed
                ------------------------------------------------- */

                else if (uploadResult.failed > 0) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Upload Incomplete - Missing Fields',
                        html: `
                            <div style="text-align:left;font-size:13.5px;color:#475569;margin-bottom:6px;">
                                Failed to process <strong>${uploadResult.failed}</strong> row${uploadResult.failed > 1 ? 's' : ''}. Please fill the missing cells listed below and re-upload:
                            </div>
                            ${renderErrorsModalHtml(uploadResult.errors)}
                        `,
                        width: '600px',
                        confirmButtonColor: '#2563EB',
                        confirmButtonText: 'I Will Fix & Re-upload',
                        customClass: {
                            popup: 'premium-swal-popup',
                        },
                    });
                }

                /* -------------------------------------------------
                   No Products
                ------------------------------------------------- */

                else {
                    Swal.fire({
                        icon: 'info',
                        title: 'No Products Processed',
                        html: uploadResult.errors.length > 0
                            ? `<div style="text-align:left;font-size:13.5px;color:#475569;margin-bottom:6px;">We couldn't process any rows due to missing fields:</div>${renderErrorsModalHtml(uploadResult.errors)}`
                            : "We couldn't find any valid rows. Please make sure mandatory fields are filled.",
                        width: uploadResult.errors.length > 0 ? '600px' : '480px',
                        confirmButtonColor: '#2563EB',
                    });
                }


                setCsvFile(null);

                setTimeout(
                    () =>
                        setResult(null),
                    8000
                );

            } catch (error: any) {

                const message =
                    error?.response?.data
                        ?.message ||
                    'Upload failed.';

                toast.error(
                    message
                );

                setResult({
                    success: 0,
                    failed: 0,
                    errors: [
                        message,
                    ],
                });

            } finally {

                setUploading(false);

            }
        };


    /* -------------------------------------------------------
       Remove File
    ------------------------------------------------------- */

    const removeFile =
        () => {
            setCsvFile(null);
            setResult(null);
        };


    return (
        <div className="bulk-upload-page">

            {/* =================================================
          PAGE HEADER
      ================================================= */}

            <div className="bulk-page-header">
                <div className="bulk-header-left">
                    <div className="bulk-header-icon">
                        <FiPackage size={22} />
                    </div>
                    <div>
                        <h1> Bulk Product Upload</h1>
                        <p>Add or update multiple products quickly using CSV or Excel. </p>
                    </div>
                </div>
                <div className="bulk-header-badge">
                    <FiCheckCircle size={15} />
                    Seller Portal
                </div>
            </div>
            {/* =================================================
          MAIN GRID
      ================================================= */}

            <div className="bulk-main-grid">


                {/* =================================================
            LEFT
        ================================================= */}

                <div className="bulk-left">


                    {/* ------------------------------------------------
              UPLOAD CARD
          ------------------------------------------------ */}

                    <div className="premium-card upload-main-card">

                        <div className="card-heading">

                            <div>

                                <div className="section-icon blue">
                                    <FiUpload size={18} />
                                </div>

                                <div>
                                    <h2>
                                        Upload your product file
                                    </h2>

                                    <p>
                                        Drag and drop your CSV or
                                        Excel file here.
                                    </p>
                                </div>

                            </div>

                            <span className="step-badge">
                                STEP 01
                            </span>

                        </div>


                        {/* ------------------------------------------------
                DROPZONE
            ------------------------------------------------ */}

                        <div
                            {...getRootProps()}
                            className={`
                modern-dropzone
                ${isDragActive ? 'drag-active' : ''}
                ${csvFile ? 'file-selected' : ''}
              `}
                        >

                            <input
                                {...getInputProps()}
                            />


                            {csvFile ? (

                                <div className="selected-file-content">

                                    <div className="file-success-icon">
                                        <BsFileSpreadsheet
                                            size={30}
                                        />
                                    </div>

                                    <div className="selected-file-info">

                                        <span className="file-status">
                                            FILE READY
                                        </span>

                                        <h3>
                                            {csvFile.name}
                                        </h3>

                                        <p>
                                            {(
                                                csvFile.size /
                                                1024
                                            ).toFixed(1)} KB
                                            <span>•</span>
                                            Ready for upload
                                        </p>

                                    </div>

                                    <button
                                        type="button"
                                        className="file-remove-button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            removeFile();
                                        }}
                                    >
                                        <FiX size={17} />
                                    </button>

                                </div>

                            ) : (

                                <div className="dropzone-content">

                                    <div className="upload-visual">

                                        <div className="upload-circle">
                                            <FiUpload
                                                size={27}
                                            />
                                        </div>

                                        <span className="upload-plus">
                                            +
                                        </span>

                                    </div>

                                    <h3>
                                        {isDragActive
                                            ? 'Drop your file here'
                                            : 'Drag & drop your file here'}
                                    </h3>

                                    <p>
                                        or click anywhere to browse
                                    </p>

                                    <div className="file-types">

                                        <span>
                                            CSV
                                        </span>

                                        <span>
                                            XLSX
                                        </span>

                                        <span>
                                            XLS
                                        </span>

                                    </div>

                                </div>

                            )}

                        </div>


                        {/* ------------------------------------------------
                DROPZONE INFO
            ------------------------------------------------ */}

                        <div className="upload-info-row">

                            <div>
                                <FiCheck size={15} />
                                Maximum file size: 10 MB
                            </div>

                            <div>
                                <FiCheck size={15} />
                                CSV / XLSX / XLS
                            </div>

                            <div>
                                <FiInfo size={15} />
                                Use our template
                            </div>

                        </div>


                        {/* ------------------------------------------------
                ACTIONS
            ------------------------------------------------ */}

                        <div className="upload-actions">

                            {csvFile && (
                                <button
                                    type="button"
                                    className="btn-secondary danger-button"
                                    onClick={removeFile}
                                >
                                    <FiX size={16} />
                                    Remove
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn-primary"
                                disabled={
                                    !csvFile ||
                                    uploading
                                }
                                onClick={handleUpload}
                            >

                                {uploading ? (
                                    <>
                                        <BiLoaderAlt
                                            size={17}
                                            className="spin"
                                        />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <FiUpload size={17} /> Upload Products
                                        <FiArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>


                {/* =================================================
            RIGHT
        ================================================= */}

                <div className="bulk-right">


                    {/* ------------------------------------------------
              RESULT CARD
          ------------------------------------------------ */}

                    <div className="premium-card result-card">

                        <div className="result-card-header">

                            <div>

                                <div className="section-icon green">
                                    <MdBarChart
                                        size={20}
                                    />
                                </div>

                                <div>
                                    <h2>
                                        Upload Results
                                    </h2>

                                    <p>
                                        Your latest upload summary
                                    </p>
                                </div>

                            </div>

                            {result && (
                                <span className="result-live">
                                    COMPLETED
                                </span>
                            )}

                        </div>


                        {result ? (

                            <>

                                <div className="result-stats">

                                    <div className="result-stat success">

                                        <div className="result-stat-icon">
                                            <FiCheckCircle
                                                size={22}
                                            />
                                        </div>

                                        <div>
                                            <strong>
                                                {result.success || 0}
                                            </strong>

                                            <span>
                                                Successfully added
                                            </span>
                                        </div>

                                    </div>


                                    <div className="result-stat failed">

                                        <div className="result-stat-icon">
                                            <FiAlertCircle
                                                size={22}
                                            />
                                        </div>

                                        <div>
                                            <strong>
                                                {result.failed || 0}
                                            </strong>

                                            <span>
                                                Failed rows
                                            </span>
                                        </div>

                                    </div>

                                </div>


                                {(result.failed || 0) === 0 && (
                                    <div className="success-message">

                                        <div className="success-message-icon">
                                            <FiCheck
                                                size={18}
                                            />
                                        </div>

                                        <div>
                                            <strong>
                                                All products uploaded successfully
                                            </strong>

                                            <span>
                                                Your product information
                                                has been processed.
                                            </span>
                                        </div>

                                    </div>
                                )}


                                {result.errors &&
                                    result.errors.length >
                                    0 && (

                                        <div className="error-log">

                                            <div className="error-log-header">

                                                <div>
                                                    <FiAlertCircle
                                                        size={17}
                                                    />

                                                    Error Details
                                                </div>

                                                <span>
                                                    {result.errors.length}
                                                </span>

                                            </div>


                                            <div className="error-list">

                                                {result.errors.map(
                                                    (error, index) => {
                                                        const cellMatch = error.match(/\(([A-Z]+[0-9]+)\)/);
                                                        const cellRef = cellMatch ? cellMatch[1] : null;
                                                        const cleanMsg = cellMatch ? error.replace(/\(([A-Z]+[0-9]+)\)\s*/, '') : error;
                                                        return (
                                                            <div
                                                                className="error-item"
                                                                key={index}
                                                                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}
                                                            >
                                                                <span>{index + 1}</span>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    {cellRef && (
                                                                        <span style={{ background: '#fee2e2', color: '#b91c1c', fontWeight: 800, fontSize: '11px', padding: '2px 7px', borderRadius: '4px', border: '1px solid #fca5a5', marginRight: '6px' }}>
                                                                            {cellRef}
                                                                        </span>
                                                                    )}
                                                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b' }}>{cleanMsg}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                )}

                                            </div>

                                        </div>

                                    )}

                            </>

                        ) : (

                            <div className="result-empty">

                                <div className="result-empty-icon">
                                    <BsBarChartSafe />
                                </div>

                                <h3>
                                    No upload yet
                                </h3>

                                <p>
                                    Upload a CSV or Excel file
                                    to see your results here.
                                </p>

                            </div>

                        )}

                    </div>


                    {/* ------------------------------------------------
              REQUIRED COLUMNS
          ------------------------------------------------ */}

                    <div className="premium-card columns-card">

                        <div className="result-card-header">

                            <div>

                                <div className="section-icon orange">
                                    <BsClipboardData
                                        size={18}
                                    />
                                </div>

                                <div>
                                    <h2>
                                        Required Information
                                    </h2>

                                    <p>
                                        Fields included in the template
                                    </p>
                                </div>

                            </div>

                        </div>


                        <div className="column-groups">

                            <div className="column-group">

                                <span className="column-group-title">
                                    PRODUCT
                                </span>

                                <div className="column-tags">

                                    <span>
                                        Products
                                    </span>

                                    <span>
                                        SKU
                                    </span>

                                </div>

                            </div>


                            <div className="column-group">

                                <span className="column-group-title">
                                    PRICING
                                </span>

                                <div className="column-tags">

                                    <span>
                                        MRP
                                    </span>

                                    <span>
                                        Selling Price
                                    </span>

                                    <span>
                                        Discount
                                    </span>

                                    <span>
                                        GST
                                    </span>

                                </div>

                            </div>


                            <div className="column-group">

                                <span className="column-group-title">
                                    INVENTORY
                                </span>

                                <div className="column-tags">

                                    <span>
                                        Stock Quantity
                                    </span>

                                </div>

                            </div>


                            <div className="column-group">

                                <span className="column-group-title">
                                    POLICY
                                </span>

                                <div className="column-tags">

                                    <span>
                                        Warranty
                                    </span>

                                    <span>
                                        Replacement Days
                                    </span>

                                    <span>
                                        Payment Options
                                    </span>

                                </div>

                            </div>

                        </div>

                    </div>
                </div>

            </div>

        </div>
    );
}


/* =========================================================
   PRODUCT SELECTION MODAL
========================================================= */

function TemplateSelectionModal({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) {

    const [
        searchTerm,
        setSearchTerm,
    ] = useState('');

    const [
        products,
        setProducts,
    ] = useState<any[]>([]);

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        hasMore,
        setHasMore,
    ] = useState(true);

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        selectedMap,
        setSelectedMap,
    ] =
        useState<Map<string, any>>(
            new Map()
        );

    const [
        format,
        setFormat,
    ] =
        useState<'xlsx' | 'csv'>(
            'xlsx'
        );

    const [
        isSelectingAll,
        setIsSelectingAll,
    ] = useState(false);

    const [
        totalCount,
        setTotalCount,
    ] = useState<number | null>(null);


    /* -------------------------------------------------------
       Open / Close
    ------------------------------------------------------- */

    useEffect(() => {

        if (isOpen) {

            setSearchTerm('');
            setProducts([]);
            setPage(1);
            setHasMore(true);
            setSelectedMap(
                new Map()
            );
            setTotalCount(null);
            setIsSelectingAll(false);

            document.body.style.overflow =
                'hidden';

        } else {

            document.body.style.overflow =
                '';

        }

        return () => {
            document.body.style.overflow =
                '';
        };

    }, [isOpen]);


    /* -------------------------------------------------------
       Fetch Products
    ------------------------------------------------------- */

    useEffect(() => {

        if (!isOpen) return;

        setLoading(true);

        const fetchProducts =
            async () => {

                if (
                    !hasMore &&
                    page > 1
                ) {
                    setLoading(false);
                    return;
                }

                try {

                    const response =
                        await productAPI
                            .getAdminTempDownloadSelector(
                                {
                                    page,
                                    limit: 20,
                                    search:
                                        searchTerm,
                                }
                            );

                    if (
                        response.data
                            .response === 'S'
                    ) {

                        const newData =
                            response.data.data ||
                            [];

                        if (typeof response.data.total === 'number') {
                            setTotalCount(response.data.total);
                        }

                        setProducts(
                            (previous) =>
                                page === 1
                                    ? newData
                                    : [
                                        ...previous,
                                        ...newData,
                                    ]
                        );

                        setHasMore(
                            newData.length === 20
                        );
                    }

                } catch (error) {

                    console.error(error);

                    toast.error(
                        'Failed to load products'
                    );

                } finally {

                    setLoading(false);

                }
            };


        const timer =
            setTimeout(
                fetchProducts,
                300
            );

        return () =>
            clearTimeout(timer);

    }, [
        page,
        searchTerm,
        isOpen,
    ]);


    /* -------------------------------------------------------
       Infinite Scroll
    ------------------------------------------------------- */

    const observer =
        useRef<
            IntersectionObserver | null
        >(null);


    const lastProductElementRef =
        useCallback(
            (node: any) => {

                if (loading) return;

                if (
                    observer.current
                ) {
                    observer.current.disconnect();
                }

                observer.current =
                    new IntersectionObserver(
                        (entries) => {

                            if (
                                entries[0]
                                    .isIntersecting &&
                                hasMore
                            ) {
                                setPage(
                                    (previous) =>
                                        previous + 1
                                );
                            }

                        }
                    );

                if (node) {
                    observer.current.observe(
                        node
                    );
                }

            },
            [
                loading,
                hasMore,
            ]
        );


    /* -------------------------------------------------------
       Search
    ------------------------------------------------------- */

    const handleSearchChange =
        (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {

            setSearchTerm(
                event.target.value
            );

            setPage(1);

            setProducts([]);

            setHasMore(true);

            setTotalCount(null);

            setIsSelectingAll(false);

        };


    /* -------------------------------------------------------
       Select All
    ------------------------------------------------------- */

    const handleSelectAll =
        async (
            checked: boolean
        ) => {

            if (checked) {

                setIsSelectingAll(true);

                const toastId =
                    toast.loading(
                        'Selecting all products...'
                    );


                try {

                    const response =
                        await productAPI
                            .getAdminTempDownloadSelector(
                                {
                                    page: 1,
                                    limit: 100000,
                                    search:
                                        searchTerm,
                                }
                            );


                    if (
                        response.data
                            .response === 'S'
                    ) {

                        const allProducts =
                            response.data.data ||
                            [];

                        const newMap = new Map();

                        allProducts.forEach(
                            (product: any) => {
                                newMap.set(
                                    product._id,
                                    product
                                );
                            }
                        );

                        setSelectedMap(newMap);

                        if (typeof response.data.total === 'number') {
                            setTotalCount(response.data.total);
                        } else {
                            setTotalCount(allProducts.length);
                        }


                        toast.success(
                            `Selected ${allProducts.length.toLocaleString('en-IN')} products`,
                            {
                                id: toastId,
                            }
                        );

                    } else {

                        toast.error(
                            'Failed to select all',
                            {
                                id: toastId,
                            }
                        );

                    }

                } catch (error) {

                    console.error(error);

                    toast.error(
                        'Failed to select all',
                        {
                            id: toastId,
                        }
                    );

                } finally {
                    setIsSelectingAll(false);
                }

            } else {

                setSelectedMap(
                    new Map()
                );

            }

        };


    /* -------------------------------------------------------
       Select Product
    ------------------------------------------------------- */

    const handleSelect =
        (
            product: any,
            checked: boolean
        ) => {

            setSelectedMap(
                (previous) => {

                    const newMap =
                        new Map(previous);

                    if (checked) {
                        newMap.set(
                            product._id,
                            product
                        );
                    } else {
                        newMap.delete(
                            product._id
                        );
                    }

                    return newMap;
                }
            );

        };


    /* -------------------------------------------------------
       Download
    ------------------------------------------------------- */

    const handleDownloadClick =
        () => {

            if (
                selectedMap.size === 0
            ) {

                toast.error(
                    'Please select at least one product'
                );

                return;
            }

            const selected =
                Array.from(
                    selectedMap.values()
                );

            downloadTemplate(
                format,
                selected
            );

            onClose();

        };


    if (!isOpen) {
        return null;
    }


    const isAllLoadedSelected =
        (totalCount !== null && totalCount > 0 && selectedMap.size >= totalCount) ||
        (products.length > 0 &&
            products.every(
                (product) =>
                    selectedMap.has(
                        product._id
                    )
            ));


    return createPortal(

        <div className="product-modal-overlay">

            <div className="product-selection-modal">


                {/* =================================================
            MODAL HEADER
        ================================================= */}

                <div className="product-modal-header">

                    <div className="modal-title-area">

                        <div className="modal-title-icon">
                            <BsGrid3X3Gap
                                size={20}
                            />
                        </div>

                        <div>

                            <h2>
                                Select Products
                            </h2>

                            <p>
                                Choose products to include
                                in your template
                            </p>

                        </div>

                    </div>


                    <button
                        type="button"
                        className="modal-close-button"
                        onClick={onClose}
                    >
                        <FiX size={19} />
                    </button>

                </div>


                {/* =================================================
            SEARCH
        ================================================= */}

                <div className="modal-search-area">

                    <div className="modal-search">

                        <FiSearch
                            size={18}
                        />

                        <input
                            type="text"
                            placeholder="Search by product name or SKU..."
                            value={searchTerm}
                            onChange={
                                handleSearchChange
                            }
                        />

                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchTerm('');
                                    setPage(1);
                                    setProducts([]);
                                    setHasMore(true);
                                    setTotalCount(null);
                                    setIsSelectingAll(false);
                                }}
                            >
                                <FiX size={15} />
                            </button>
                        )}

                    </div>

                </div>


                {/* =================================================
            TOOLBAR
        ================================================= */}

                <div className="product-modal-toolbar">

                    <button
                        type="button"
                        disabled={isSelectingAll}
                        className={`select-all-button ${isAllLoadedSelected
                            ? 'selected'
                            : ''
                            }`}
                        onClick={() =>
                            handleSelectAll(
                                !isAllLoadedSelected
                            )
                        }
                    >

                        <span className="custom-checkbox">

                            {isSelectingAll ? (
                                <BiLoaderAlt
                                    size={13}
                                    className="spin"
                                />
                            ) : isAllLoadedSelected ? (
                                <FiCheck
                                    size={13}
                                />
                            ) : null}

                        </span>

                        {isSelectingAll
                            ? 'Selecting All...'
                            : isAllLoadedSelected
                                ? 'Clear All'
                                : 'Select All'}

                    </button>


                    <span className="product-count">
                        {loading && page === 1 ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <BiLoaderAlt size={14} className="spin" /> Loading...
                            </span>
                        ) : totalCount !== null ? (
                            `${totalCount.toLocaleString('en-IN')} products`
                        ) : (
                            `${products.length} products`
                        )}
                    </span>

                </div>


                {/* =================================================
            PRODUCT LIST
        ================================================= */}

                <div className="product-list-container">

                    {isSelectingAll ? (
                        <div className="product-list-loading" style={{ padding: '60px 0' }}>
                            <BiLoaderAlt
                                size={32}
                                className="spin"
                                style={{ color: '#0284c7' }}
                            />
                            <span style={{ marginTop: '12px', fontWeight: '600', color: '#0284c7', fontSize: '14px' }}>
                                Fetching all {totalCount !== null ? totalCount.toLocaleString('en-IN') : ''} products... Please wait.
                            </span>
                        </div>
                    ) : (
                        <>
                            {products.map(
                                (
                                    product,
                                    index
                                ) => {

                                    const isTargetNode =
                                        products.length > 3 &&
                                        index ===
                                        products.length - 3;

                                    const isSelected =
                                        selectedMap.has(
                                            product._id
                                        );

                                    const image =
                                        product.images &&
                                            product.images.length >
                                            0
                                            ? product.images[0]?.url ||
                                            product.images[0]
                                            : null;


                                    return (

                                        <div
                                            key={
                                                product._id
                                            }
                                            ref={
                                                isTargetNode
                                                    ? lastProductElementRef
                                                    : null
                                            }
                                            className={`product-select-row ${isSelected
                                                ? 'selected'
                                                : ''
                                                }`}
                                            onClick={() =>
                                                handleSelect(
                                                    product,
                                                    !isSelected
                                                )
                                            }
                                        >

                                            {/* CHECKBOX */}

                                            <div
                                                className={`product-checkbox ${isSelected
                                                    ? 'checked'
                                                    : ''
                                                    }`}
                                            >

                                                {isSelected && (
                                                    <FiCheck
                                                        size={14}
                                                        strokeWidth={3}
                                                    />
                                                )}

                                            </div>


                                            {/* IMAGE */}

                                            <div className="product-image">

                                                {image ? (

                                                    <img
                                                        src={image}
                                                        alt={
                                                            product.name
                                                        }
                                                    />

                                                ) : (

                                                    <BsBox
                                                        size={24}
                                                    />

                                                )}

                                            </div>


                                            {/* INFO */}

                                            <div className="product-row-info">

                                                <h3>
                                                    {product.name ||
                                                        'Unnamed Product'}
                                                </h3>

                                                <div className="product-meta">

                                                    <span className="sku-badge">
                                                        {product.sku ||
                                                            'No SKU'}
                                                    </span>

                                                    {product.mrp && (
                                                        <span className="product-price">
                                                            ₹
                                                            {Number(
                                                                product.mrp
                                                            ).toLocaleString(
                                                                'en-IN'
                                                            )}
                                                        </span>
                                                    )}

                                                </div>

                                            </div>


                                            {/* SELECTED */}

                                            {isSelected && (
                                                <div className="selected-label">
                                                    Selected
                                                </div>
                                            )}

                                        </div>

                                    );

                                }
                            )}


                            {/* LOADING */}

                            {loading && (

                                <div className="product-list-loading">

                                    <BiLoaderAlt
                                        size={26}
                                        className="spin"
                                    />

                                    <span>
                                        Loading products...
                                    </span>

                                </div>

                            )}


                            {/* EMPTY */}

                            {!loading &&
                                products.length === 0 && (

                                    <div className="product-empty">

                                        <div className="product-empty-icon">
                                            <BsGrid3X3Gap
                                                size={30}
                                            />
                                        </div>

                                        <h3>
                                            No products found
                                        </h3>

                                        <p>
                                            Try another product
                                            name or SKU.
                                        </p>

                                        {searchTerm && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setPage(1);
                                                    setProducts([]);
                                                    setHasMore(true);
                                                    setTotalCount(null);
                                                    setIsSelectingAll(false);
                                                }}
                                            >
                                                <FiRefreshCw
                                                    size={14}
                                                />
                                                Clear Search
                                            </button>
                                        )}

                                    </div>

                                )}
                        </>
                    )}

                </div>

                <div className="product-modal-footer">
                    <div className="selection-summary">
                        {isSelectingAll ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0284c7', fontWeight: '600' }}>
                                <BiLoaderAlt size={18} className="spin" />
                                <span>Selecting all products...</span>
                            </div>
                        ) : (
                            <>
                                <strong> {selectedMap.size.toLocaleString('en-IN')} </strong>
                                <span> products selected </span>
                            </>
                        )}
                    </div>

                    <div className="modal-footer-actions">
                        <div className="format-select-wrapper">
                            <FiFileText size={15} />
                            <select
                                value={format}
                                onChange={(event) =>
                                    setFormat(event.target.value as 'xlsx' | 'csv')
                                }
                            >
                                <option value="xlsx"> Excel (.xlsx) </option>
                                <option value="csv"> CSV (.csv) </option>
                            </select>
                            <FiChevronDown size={14} />
                        </div>
                        <button
                            type="button"
                            className="modal-download-button"
                            disabled={selectedMap.size === 0}
                            onClick={handleDownloadClick}
                        >
                            <FiDownload size={16} /> Download Template
                        </button>
                    </div>
                </div>
            </div>
        </div>,

        document.body
    );
}


/* =========================================================
   HELPER ICON
========================================================= */

function BsBarChartSafe() {
    return (
        <MdBarChart
            size={30}
        />
    );
}


/* =========================================================
   EXPORTS
========================================================= */

export {
    CSVUploadPanel,
    TemplateSelectionModal,
    downloadTemplate,
};